import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { fmtDataHora as fmtFull } from "../lib";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const labelStatus = (s) => ({
  pendente: "AGUARDANDO SAÍDA",
  em_transito: "EM TRÂNSITO",
  entregue: "ENTREGUE",
  cancelado: "CANCELADO",
})[s] || s;

const statusColor = (s) => ({
  pendente: "#cdc8be",
  em_transito: "#b8935a",
  entregue: "#5fa86c",
  cancelado: "#e35d5d",
})[s] || "#cdc8be";

/**
 * Painel de Fretes embutido no detalhe da obra (admin/gestor).
 * - Lista fretes da obra, status e link público
 * - Botão "Criar Frete" abre modal para escolher frentista + materiais
 */
export default function FretesPanel({ obraId, obraNome }) {
  const [fretes, setFretes] = useState([]);
  const [frentistas, setFrentistas] = useState([]);
  const [marmorarias, setMarmorarias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [statusModal, setStatusModal] = useState(null); // frete sendo editado
  const [form, setForm] = useState({
    frentista_id: "",
    materiais: "",
    marmoraria_origem_id: "",
    endereco_retirada: "",
    endereco_entrega: "",
    observacao: "",
  });
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null); // frete em edição (reusa o formulário)
  const FORM_VAZIO = { frentista_id: "", materiais: "", marmoraria_origem_id: "", endereco_retirada: "", endereco_entrega: "", observacao: "" };
  const abrirEdicao = (f) => {
    setEditId(f.id);
    setForm({
      frentista_id: f.terceirizado_id || (frentistas.find(x => x.user_id === f.frentista_id) || {}).id || "",
      materiais: (f.materiais || []).join("\n"),
      marmoraria_origem_id: f.marmoraria_origem_id || "",
      endereco_retirada: f.endereco_retirada || "",
      endereco_entrega: f.endereco_entrega || "",
      observacao: f.observacao || "",
    });
    setShowForm(true);
    document.querySelector('[data-testid="fretes-panel"]')?.scrollIntoView({ behavior: "smooth" });
  };
  const fecharForm = () => { setShowForm(false); setEditId(null); setForm(FORM_VAZIO); };

  const token = localStorage.getItem("cps_token");
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const [fr, frt, terc] = await Promise.all([
        axios.get(`${API}/fretes?obra_id=${obraId}`, { headers }),
        axios.get(`${API}/frentistas`, { headers }),
        axios.get(`${API}/terceirizados`, { headers }),
      ]);
      setFretes(fr.data || []);
      setFrentistas(frt.data || []);
      // filtra apenas marmorarias (origem do material)
      setMarmorarias((terc.data || []).filter(t => t.tipo === "marmoraria"));
    } catch (e) {
      console.error("[Fretes] load:", e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [obraId]);

  useEffect(() => {
    load();
    const id = setInterval(load, 20000); // refresh status em tempo real
    return () => clearInterval(id);
  }, [load]);

  const criarFrete = async () => {
    if (!form.frentista_id && !editId) return alert("Selecione um frentista");
    setSaving(true);
    try {
      const materiais = form.materiais.split("\n").map(s => s.trim()).filter(Boolean);
      const body = {
        ...(form.frentista_id ? { terceirizado_id: form.frentista_id } : {}),  // unificado: dropdown retorna terceirizado.id
        materiais,
        marmoraria_origem_id: form.marmoraria_origem_id || null,
        endereco_retirada: form.endereco_retirada.trim(),
        endereco_entrega: form.endereco_entrega.trim(),
        observacao: form.observacao.trim() || null,
      };
      if (editId) await axios.patch(`${API}/fretes/${editId}`, body, { headers });
      else await axios.post(`${API}/fretes`, { obra_id: obraId, ...body }, { headers });
      fecharForm();
      await load();
    } catch (e) {
      alert("Erro: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const removerFrete = async (id) => {
    if (!window.confirm("Remover este frete?")) return;
    try {
      await axios.delete(`${API}/fretes/${id}`, { headers });
      await load();
    } catch (e) {
      alert("Erro: " + (e?.response?.data?.detail || e.message));
    }
  };

  if (loading) return <div style={{ padding: 24, color: "#8a857a", fontSize: 13 }}>Carregando fretes…</div>;

  return (
    <div data-testid="fretes-panel" style={{ padding: "8px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <h3 style={{ margin: 0, fontFamily: "'Fraunces',serif", fontSize: 18, color: "#f5f0e8", fontWeight: 400 }}>
          Entregas / Fretes
        </h3>
        <button
          onClick={() => (showForm ? fecharForm() : setShowForm(true))}
          data-testid="fretes-novo-btn"
          style={{
            background: showForm ? "transparent" : "#b8935a",
            color: showForm ? "#cdc8be" : "#0d0d0f",
            border: showForm ? "1px solid #2b2833" : "none",
            padding: "8px 16px", borderRadius: 4, cursor: "pointer",
            fontSize: 12, fontWeight: 600, letterSpacing: 0.5,
          }}
        >
          {showForm ? "Cancelar" : "+ Criar Frete"}
        </button>
      </div>

      {showForm && (
        <div style={{ background: "#15141a", border: "1px solid #2b2833", borderRadius: 6, padding: 18, marginBottom: 18 }} data-testid="fretes-form">
          <div style={{ marginBottom: 12 }}>
            <label style={lbl}>Frentista</label>
            {frentistas.length === 0 ? (
              <div style={{ color: "#e35d5d", fontSize: 12, padding: "8px 0" }}>
                Nenhum frentista cadastrado. Cadastre um <strong>Terceirizado</strong> com tipo <strong>Frentista</strong> e habilite o login.
              </div>
            ) : (
              <select
                value={form.frentista_id}
                onChange={(e) => setForm({ ...form, frentista_id: e.target.value })}
                data-testid="fretes-form-frentista"
                style={inp}
              >
                <option value="">— Selecione —</option>
                {frentistas.map(f => (
                  <option key={f.id} value={f.id} disabled={!f.tem_login}>
                    {f.nome}{f.empresa && f.empresa !== f.nome ? ` · ${f.empresa}` : ""}
                    {!f.tem_login && "   🚫 SEM LOGIN — habilite na tela Terceirizados"}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={lbl}>Marmoraria de Origem</label>
            {marmorarias.length === 0 ? (
              <div style={{ color: "#8a857a", fontSize: 12, padding: "6px 0" }}>
                Nenhuma marmoraria cadastrada como Terceirizado (tipo=marmoraria).
              </div>
            ) : (
              <select
                value={form.marmoraria_origem_id}
                onChange={(e) => {
                  const id = e.target.value;
                  const m = marmorarias.find(x => x.id === id);
                  setForm({
                    ...form,
                    marmoraria_origem_id: id,
                    // pré-preenche endereço de retirada com o cadastro da marmoraria, se houver
                    endereco_retirada: form.endereco_retirada || (m?.endereco || ""),
                  });
                }}
                data-testid="fretes-form-marmoraria"
                style={inp}
              >
                <option value="">— Selecione (opcional) —</option>
                {marmorarias.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.nome}{m.empresa && m.empresa !== m.nome ? ` · ${m.empresa}` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={lbl}>Endereço de retirada</label>
            <input
              type="text"
              value={form.endereco_retirada}
              onChange={(e) => setForm({ ...form, endereco_retirada: e.target.value })}
              placeholder="Onde o frentista busca o material (ex: depósito da marmoraria)"
              data-testid="fretes-form-retirada"
              style={inp}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={lbl}>Endereço de entrega</label>
            <input
              type="text"
              value={form.endereco_entrega}
              onChange={(e) => setForm({ ...form, endereco_entrega: e.target.value })}
              placeholder="Default: cidade/estado da obra"
              data-testid="fretes-form-endereco"
              style={inp}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={lbl}>
              Observação <span style={{ color: "#8a857a", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>
                (opcional · descreve o que vai no frete)
              </span>
            </label>
            <input
              type="text"
              value={form.observacao}
              onChange={(e) => setForm({ ...form, observacao: e.target.value.slice(0, 100) })}
              maxLength={100}
              placeholder="Ex: Válvulas, Cuba, Lote principal, Ferramentas..."
              data-testid="fretes-form-observacao"
              style={inp}
            />
            <div style={{ fontSize: 10, color: "#8a857a", textAlign: "right", marginTop: 4 }}>
              {form.observacao.length}/100
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={lbl}>Materiais (1 por linha)</label>
            <textarea
              value={form.materiais}
              onChange={(e) => setForm({ ...form, materiais: e.target.value })}
              placeholder={"Bancada Calacatta 2,5m²\nSoleiras (4 unidades)\nRodapés"}
              rows={4}
              data-testid="fretes-form-materiais"
              style={{ ...inp, resize: "vertical", fontFamily: "inherit" }}
            />
          </div>
          <button
            onClick={criarFrete}
            disabled={saving || (!form.frentista_id && !editId)}
            data-testid="fretes-form-salvar"
            style={{
              background: "#b8935a", color: "#0d0d0f", border: "none",
              padding: "10px 18px", fontWeight: 600, fontSize: 13,
              borderRadius: 4, cursor: "pointer", letterSpacing: 0.5,
            }}
          >
            {saving ? "Salvando…" : editId ? "Salvar alterações" : "Criar Frete"}
          </button>
        </div>
      )}

      {fretes.length === 0 ? (
        <div style={{ background: "#15141a", border: "1px dashed #2b2833", padding: 28, textAlign: "center", color: "#8a857a", borderRadius: 6, fontSize: 13 }} data-testid="fretes-vazio">
          Nenhuma entrega registrada para esta obra.
        </div>
      ) : (
        fretes.map(f => (
          <div key={f.id} style={{ background: "#15141a", border: "1px solid #2b2833", borderRadius: 6, padding: 16, marginBottom: 12 }} data-testid={`fretes-item-${f.id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <button
                    type="button"
                    onClick={() => setStatusModal(f)}
                    data-testid={`fretes-status-btn-${f.id}`}
                    title="Clique para alterar o status do frete"
                    style={{
                      fontSize: 10, letterSpacing: 1.5, padding: "3px 10px",
                      borderRadius: 3, fontWeight: 600,
                      color: statusColor(f.status),
                      border: `1px solid ${statusColor(f.status)}`,
                      background: "transparent",
                      cursor: "pointer",
                      display: "inline-flex", alignItems: "center", gap: 6,
                      transition: "all .15s ease",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = `${statusColor(f.status)}22`; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                  >
                    {labelStatus(f.status)} <span style={{ fontSize: 9, opacity: 0.6 }}>▾</span>
                  </button>
                  <span style={{ fontSize: 12, color: "#8a857a" }}>· {f.frentista_nome || "—"}</span>
                </div>
                <div style={{ fontSize: 13, color: "#cdc8be", marginTop: 4, lineHeight: 1.55 }}>
                  {f.observacao && (
                    <div
                      data-testid={`fretes-observacao-${f.id}`}
                      style={{
                        fontSize: 12, color: "#cdc8be", marginBottom: 6,
                        background: "rgba(184,147,90,0.08)",
                        padding: "5px 10px",
                        borderLeft: "2px solid #b8935a",
                        fontStyle: "italic",
                      }}
                    >
                      📦 {f.observacao}
                    </div>
                  )}
                  {(f.marmoraria_origem_nome || f.endereco_retirada) && (
                    <div style={{ fontSize: 12, color: "#8a857a", marginBottom: 3 }} data-testid={`fretes-retirada-${f.id}`}>
                      <span style={{ color: "#b8935a" }}>📍 Retirada:</span>{" "}
                      {f.marmoraria_origem_nome ? <strong style={{ color: "#cdc8be" }}>{f.marmoraria_origem_nome}</strong> : null}
                      {f.marmoraria_origem_nome && f.endereco_retirada ? " — " : ""}
                      {f.endereco_retirada || (!f.marmoraria_origem_nome ? <em>sem endereço</em> : null)}
                    </div>
                  )}
                  <div data-testid={`fretes-entrega-${f.id}`}>
                    <span style={{ color: "#5fa86c", fontSize: 12 }}>🏁 Entrega:</span>{" "}
                    {f.endereco_entrega || <em style={{ color: "#8a857a" }}>sem endereço</em>}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 14, fontSize: 11, color: "#8a857a", marginTop: 6, flexWrap: "wrap" }}>
                  <span>📅 Criado {fmtFull(f.criado_em)}</span>
                  {f.saida_em && <span>🚚 Saída {fmtFull(f.saida_em)}</span>}
                  {f.chegada_em && <span>🏁 Entregue {fmtFull(f.chegada_em)}</span>}
                  {f.recebido_por && (
                    <span style={{ color: "#5fa86c" }} data-testid={`fretes-recebidopor-${f.id}`}>
                      ✓ Recebido por <strong>{f.recebido_por}</strong>
                    </span>
                  )}
                </div>
                {(f.historico_status || []).length > 0 && (
                  <details style={{ marginTop: 8 }} data-testid={`fretes-historico-${f.id}`}>
                    <summary style={{
                      fontSize: 10, color: "#8a857a", letterSpacing: 1.2, textTransform: "uppercase",
                      cursor: "pointer", userSelect: "none",
                    }}>
                      Histórico de status ({f.historico_status.length})
                    </summary>
                    <ul style={{ margin: "6px 0 0", padding: "0 0 0 16px", fontSize: 11, color: "#cdc8be", lineHeight: 1.55 }}>
                      {f.historico_status.map((h, i) => (
                        <li key={i} style={{ color: "#8a857a" }}>
                          <span style={{ color: statusColor(h.para) }}>{labelStatus(h.para)}</span>
                          <span> · {fmtFull(h.em)}</span>
                          {h.por_user_nome && <span> · por {h.por_user_nome}</span>}
                          {h.recebido_por && <span style={{ color: "#5fa86c" }}> · recebido por {h.recebido_por}</span>}
                          {h.nota && <div style={{ marginLeft: 0, fontStyle: "italic", color: "#8a857a" }}>"{h.nota}"</div>}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
                {(f.materiais || []).length > 0 && (
                  <ul style={{ margin: "8px 0 0", padding: "0 0 0 16px", color: "#cdc8be", fontSize: 12, lineHeight: 1.6 }}>
                    {f.materiais.slice(0, 3).map((m, i) => <li key={i}>{m}</li>)}
                    {f.materiais.length > 3 && <li style={{ color: "#8a857a" }}>+{f.materiais.length - 3} item(s)…</li>}
                  </ul>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
                {f.tracking_token && (
                  <a
                    href={`/rastreio/${f.tracking_token}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`fretes-link-${f.id}`}
                    style={{
                      fontSize: 11, color: "#b8935a", textDecoration: "none",
                      border: "1px solid #b8935a", padding: "5px 10px", borderRadius: 3,
                      letterSpacing: 0.5, fontWeight: 600,
                    }}
                  >📍 RASTREAR</a>
                )}
                <button
                  onClick={() => abrirEdicao(f)}
                  data-testid={`fretes-editar-${f.id}`}
                  style={{
                    fontSize: 10, color: "#b8935a", background: "transparent",
                    border: "1px solid #2b2833", padding: "3px 8px", borderRadius: 3,
                    cursor: "pointer", letterSpacing: 0.5, textTransform: "uppercase",
                  }}
                >✎ Editar</button>
                {f.status !== "entregue" && (
                  <button
                    onClick={() => removerFrete(f.id)}
                    data-testid={`fretes-remover-${f.id}`}
                    style={{
                      fontSize: 10, color: "#8a857a", background: "transparent",
                      border: "1px solid #2b2833", padding: "3px 8px", borderRadius: 3,
                      cursor: "pointer", letterSpacing: 0.5, textTransform: "uppercase",
                    }}
                  >Remover</button>
                )}
              </div>
            </div>
          </div>
        ))
      )}
      {statusModal && (
        <MudarStatusModal
          frete={statusModal}
          onClose={() => setStatusModal(null)}
          onSaved={async () => { setStatusModal(null); await load(); }}
        />
      )}
    </div>
  );
}

function MudarStatusModal({ frete, onClose, onSaved }) {
  const STATUS_NEXT = {
    pendente: ["em_transito", "entregue", "cancelado"],
    em_transito: ["entregue", "cancelado"],
    entregue: [],
    cancelado: ["pendente"],
  };
  const opts = STATUS_NEXT[frete.status || "pendente"] || [];
  const [novoStatus, setNovoStatus] = useState(opts[0] || "");
  const [dataEvento, setDataEvento] = useState(() => {
    // default = agora, formato datetime-local: YYYY-MM-DDTHH:mm
    const d = new Date();
    const pad = n => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const [recebidoPor, setRecebidoPor] = useState("");
  const [nota, setNota] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const token = localStorage.getItem("cps_token");

  const submit = async () => {
    setErr(""); setSaving(true);
    try {
      const body = {
        status: novoStatus,
        data_evento: dataEvento, // backend aceita "YYYY-MM-DDTHH:mm"
      };
      if (novoStatus === "entregue" && recebidoPor.trim()) body.recebido_por = recebidoPor.trim();
      if (nota.trim()) body.nota = nota.trim();
      await axios.post(`${API}/fretes/${frete.id}/status`, body, { headers: { Authorization: `Bearer ${token}` } });
      await onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Erro ao alterar status");
    } finally { setSaving(false); }
  };

  if (opts.length === 0) {
    return (
      <ModalShell onClose={onClose} title="Frete já entregue">
        <p style={{ color: "#cdc8be", fontSize: 13, lineHeight: 1.6 }}>
          Este frete já foi marcado como <strong style={{ color: "#5fa86c" }}>ENTREGUE</strong> em {new Date(frete.chegada_em).toLocaleString("pt-BR")}
          {frete.recebido_por && <> · recebido por <strong>{frete.recebido_por}</strong></>}.
        </p>
        <p style={{ color: "#8a857a", fontSize: 12, marginTop: 8 }}>
          Status terminal — não pode ser alterado para preservar o histórico de entregas.
        </p>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <button onClick={onClose} style={btnGhost} data-testid="status-modal-close">Fechar</button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell onClose={onClose} title={`Alterar status do frete`}>
      <div style={{ marginBottom: 12, fontSize: 12, color: "#8a857a" }}>
        Status atual: <strong style={{ color: "#cdc8be" }}>{({
          pendente: "AGUARDANDO SAÍDA", em_transito: "EM TRÂNSITO",
          entregue: "ENTREGUE", cancelado: "CANCELADO",
        })[frete.status] || frete.status}</strong>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={lbl}>Novo status</label>
        <select
          value={novoStatus}
          onChange={e => setNovoStatus(e.target.value)}
          style={inp}
          data-testid="status-modal-select"
        >
          {opts.map(s => (
            <option key={s} value={s}>{({
              pendente: "AGUARDANDO SAÍDA", em_transito: "EM TRÂNSITO",
              entregue: "ENTREGUE", cancelado: "CANCELADO",
            })[s]}</option>
          ))}
        </select>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={lbl}>Data/hora do evento</label>
        <input
          type="datetime-local"
          value={dataEvento}
          onChange={e => setDataEvento(e.target.value)}
          style={inp}
          data-testid="status-modal-data"
        />
        <div style={{ fontSize: 10, color: "#8a857a", marginTop: 4 }}>
          Use data retroativa se o evento já ocorreu na vida real.
        </div>
      </div>
      {novoStatus === "entregue" && (
        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>Recebido por (opcional)</label>
          <input
            type="text"
            value={recebidoPor}
            onChange={e => setRecebidoPor(e.target.value.slice(0, 80))}
            placeholder="Ex: João Silva (gerente da obra)"
            style={inp}
            maxLength={80}
            data-testid="status-modal-recebidopor"
          />
        </div>
      )}
      <div style={{ marginBottom: 12 }}>
        <label style={lbl}>Nota (opcional)</label>
        <input
          type="text"
          value={nota}
          onChange={e => setNota(e.target.value.slice(0, 200))}
          placeholder="Ex: Entrega confirmada por WhatsApp"
          style={inp}
          maxLength={200}
          data-testid="status-modal-nota"
        />
      </div>
      {err && (
        <div data-testid="status-modal-err" style={{
          padding: "8px 12px", background: "rgba(196,122,122,0.1)",
          border: "1px solid rgba(196,122,122,0.3)", color: "#c47a7a",
          fontSize: 12, marginBottom: 12,
        }}>⚠ {err}</div>
      )}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button onClick={onClose} disabled={saving} style={btnGhost}>Cancelar</button>
        <button
          onClick={submit}
          disabled={saving || !novoStatus}
          style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}
          data-testid="status-modal-submit"
        >
          {saving ? "Salvando…" : "Confirmar alteração"}
        </button>
      </div>
    </ModalShell>
  );
}

function ModalShell({ children, onClose, title }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
        backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 9999, padding: 20,
      }}
      data-testid="modal-overlay"
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#15141a", border: "1px solid #2b2833",
          padding: 24, maxWidth: 500, width: "100%",
          boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
        }}
      >
        <h3 style={{
          fontFamily: "'Fraunces',serif", fontSize: 22,
          color: "#f5f0e8", margin: "0 0 18px", fontWeight: 400,
        }}>{title}</h3>
        {children}
      </div>
    </div>
  );
}

const btnGhost = {
  background: "transparent", color: "#cdc8be",
  border: "1px solid #2b2833", padding: "8px 16px",
  fontSize: 12, letterSpacing: 0.5, cursor: "pointer",
};
const btnPrimary = {
  background: "#b8935a", color: "#0d0d0f", border: "none",
  padding: "8px 18px", fontSize: 12, fontWeight: 600,
  letterSpacing: 0.5, cursor: "pointer",
};

const lbl = { display: "block", fontSize: 10, color: "#8a857a", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 5 };
const inp = {
  width: "100%", background: "#0d0d0f", border: "1px solid #2b2833",
  color: "#f5f0e8", padding: "8px 10px", fontSize: 13, borderRadius: 3,
  boxSizing: "border-box",
};
